// Intercepteur fetch centralisé (prérequis léger de S4).
// - Injecte Authorization: Bearer <token> UNIQUEMENT pour les appels vers notre backend
//   ET seulement si l'appelant n'a pas déjà posé ce header (rétro-compat stricte).
// - Ne touche jamais aux appels tiers, ni au body (FormData/streaming intacts).
// - Expose un point d'extension unique (handleWithAuthRetry) pour un futur refresh-on-401.

export function installFetchInterceptor() {
  if (typeof window === 'undefined' || !window.fetch || window.fetch.__isPatched) {
    return;
  }

  const { getPreviewToken } = require('./previewAuth');
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const originalFetch = window.fetch.bind(window);

  // Détecte si une URL cible notre propre backend (absolue via REACT_APP_BACKEND_URL,
  // ou chemin relatif /api sur la même origine).
  function isOurBackend(rawUrl) {
    try {
      const absolute = new URL(rawUrl, window.location.origin);
      const isRelativeApi =
        absolute.origin === window.location.origin && absolute.pathname.startsWith('/api');
      if (!backendBase) return isRelativeApi;
      const backendUrl = new URL(backendBase);
      const sameBackendOrigin =
        absolute.origin === backendUrl.origin && absolute.pathname.startsWith('/api');
      return sameBackendOrigin || isRelativeApi;
    } catch {
      return false;
    }
  }

  // Point d'extension : retry-on-401 via refresh token (S4).
  async function handleWithAuthRetry(request) {
    // Cloner AVANT consommation pour pouvoir rejouer un POST/PUT avec body.
    const retryable = request.clone();
    const response = await originalFetch(request);

    if (response.status !== 401) return response;

    // Mode preview (iframe dev) : ne jamais tenter de refresh (éviterait d'écrire
    // dans le storage partagé et de casser la session admin parente).
    if (getPreviewToken()) return response;

    // Ne pas tenter de refresh sur les endpoints d'auth eux-mêmes (anti-boucle).
    if (request.url.includes('/api/auth/refresh') || request.url.includes('/api/auth/login')) {
      return response;
    }

    const { refreshSession } = await import('./refreshSession');
    const newToken = await refreshSession();
    if (!newToken) return response; // laisser le 401 remonter (logout géré ailleurs)

    const retryHeaders = new Headers(retryable.headers || undefined);
    retryHeaders.set('Authorization', `Bearer ${newToken}`);
    return originalFetch(new Request(retryable, { headers: retryHeaders }));
  }

  async function interceptedFetch(input, init) {
    const request = input instanceof Request ? input : new Request(input, init);

    if (!isOurBackend(request.url)) {
      return originalFetch(input, init);
    }

    const headers = new Headers(request.headers || undefined);

    if (!headers.has('Authorization')) {
      let token = null;
      try {
        token = getPreviewToken() ||
                window.sessionStorage?.getItem('impersonate_token') ||
                window.localStorage?.getItem('token');
      } catch {
        token = null;
      }
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    // Bac à sable (formation) : ajoute l'en-tête de bascule DB si le mode est actif.
    try {
      if (window.localStorage?.getItem('pitboard_sandbox') === '1' &&
          !headers.has('X-Pitboard-Sandbox')) {
        headers.set('X-Pitboard-Sandbox', '1');
      }
    } catch {
      /* ignore */
    }

    return handleWithAuthRetry(new Request(request, { headers }));
  }

  interceptedFetch.__isPatched = true;
  interceptedFetch.__originalFetch = originalFetch;
  window.fetch = interceptedFetch;
}
